import { useEffect } from 'react'
import { useRouter } from 'next/router'
import { useQueryClient } from 'react-query'
import { getCookie } from 'cookies-next'

import { BASE_API_URL } from '@/configs'
import { QUERY_KEYS } from '@/lib/cache-keys/queryKeys'
import { PREFIXES } from 'api/constants'
import { walletApi } from 'api/walletApi'
import { useAuth } from 'context/JWTContext'
import { useWalletStore } from 'store/walletStore'
import { IWalletBalance, WalletSocketMessage } from 'types/prize-draw/wallet'

const WALLET_SOCKET_URL = `${BASE_API_URL.replace(/^http/, 'ws')}/${PREFIXES.GAME}/ws/wallet`

const CLOSE_CODE_UNAUTHORIZED = 4401
const NO_RETRY_CLOSE_CODES = [4403, 4429]
const MAX_RECONNECT_DELAY_MS = 30_000

type WalletSocketListener = (message: WalletSocketMessage) => void

const listeners = new Set<WalletSocketListener>()

// Listeners are called after the balance has been refetched and written to the store.
export const subscribeToWalletSocket = (listener: WalletSocketListener) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

const getReconnectDelay = (attempt: number) => Math.min(1000 * 2 ** attempt, MAX_RECONNECT_DELAY_MS)

// One socket per tab - mount once, in _app. The server allows 5 sockets per user.
export function useWalletSocket() {
  const router = useRouter()
  const queryClient = useQueryClient()
  const { isAuthenticated } = useAuth()

  const enabled = isAuthenticated && router.pathname.includes('/prize-draw')

  useEffect(() => {
    if (!enabled || !BASE_API_URL) return

    let socket: WebSocket | null = null
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined
    let reconnectAttempt = 0
    let authRetried = false
    let stopped = false

    const refreshWallet = async () => {
      const balance = await queryClient
        .fetchQuery([QUERY_KEYS.WALLET_BALANCE], () => walletApi.getWalletBalance(), {
          staleTime: 0,
        })
        .catch((): IWalletBalance | null => null)
      if (stopped) return

      if (balance) useWalletStore.getState().setWalletBalance(balance)
      queryClient.invalidateQueries([QUERY_KEYS.WALLET_TRANSACTIONS])
    }

    const handleMessage = async (message: WalletSocketMessage) => {
      if (message.type === 'auth.ok') {
        reconnectAttempt = 0
        authRetried = false
      }
      await refreshWallet()
      if (stopped) return
      listeners.forEach((listener) => listener(message))
    }

    const connect = () => {
      const token = getCookie('accessToken')
      if (!token) return

      const ws = new WebSocket(WALLET_SOCKET_URL)
      socket = ws

      ws.onopen = () => ws.send(JSON.stringify({ type: 'auth', token }))

      ws.onmessage = ({ data }) => {
        let message: WalletSocketMessage
        try {
          message = JSON.parse(data)
        } catch {
          return
        }
        if (!message || typeof message !== 'object') return
        if (message.type === 'auth.ok' || message.type === 'wallet.updated') {
          handleMessage(message)
        }
      }

      ws.onclose = ({ code }) => {
        socket = null
        if (stopped) return

        if (NO_RETRY_CLOSE_CODES.includes(code)) return

        // Bad/expired token: retry once with whatever the cookie holds now.
        if (code === CLOSE_CODE_UNAUTHORIZED) {
          if (authRetried) return
          authRetried = true
          connect()
          return
        }

        reconnectTimer = setTimeout(() => {
          authRetried = false
          connect()
        }, getReconnectDelay(reconnectAttempt))
        reconnectAttempt += 1
      }
    }

    connect()

    return () => {
      stopped = true
      clearTimeout(reconnectTimer)
      socket?.close(1000)
    }
  }, [enabled, queryClient])
}
